import React, { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { adminService } from '../../../services/admin'
import { apiErrorMessage, createMessageThread, getAccessContext, getScheduleShiftDetail, getWorkerProfile, normalizeApiError } from '../../../services/access'
import { setApiAuthToken, setApiTenantContext } from '../../../services/api'
import type { AccessibleLocation, LiveWorkerLocationDto, WorkerBreadcrumbTrailDto } from '../../../types/access'
import { useI18n } from '../../../i18n/I18nContext'
import { DetailDrawer, ErrorBanner, LoadingState } from './AdminUiPrimitives'
import {
  countLiveWorkers,
  firstText,
  isFallbackLocation,
  locationSourceKey,
  validMapPoint,
  workerStatusLabel,
  workerStatusMarkerColors,
} from './liveMapWorkerStatus'
import { liveWorkerInitials, reconcileSelectedLiveWorker } from './liveMapState'

const OSM_MAX_ZOOM = 19

type TimeRangePreset = '30m' | '2h' | '12h' | 'today' | 'custom'
const LIVE_MAP_POLL_MS = 15_000
const MAX_TRANSIENT_POLL_FAILURES = 3
const TRANSIENT_POLL_STATUSES = new Set([408, 429, 500, 502, 503, 504])
const preservedViewerState = new Map<string, { selectedWorkerId: string; rangePreset: TimeRangePreset; customFrom: string; customTo: string; center?: [number, number]; zoom?: number }>()

function locationName(location: AccessibleLocation) {
  return location.name || location.label || location.location_key || 'Location'
}

function isLocationTrackingEnabled(location: AccessibleLocation) {
  const raw = location as AccessibleLocation & {
    enableActiveTracking?: unknown
    enable_active_tracking?: unknown
    active_tracking?: unknown
    metadata?: Record<string, unknown>
  }

  return Boolean(
    raw.activeTracking
    || raw.trackingEnabled
    || raw.enableActiveTracking
    || raw.enable_active_tracking
    || raw.active_tracking
    || raw.metadata?.activeTracking
    || raw.metadata?.trackingEnabled
    || raw.metadata?.enableActiveTracking
    || raw.metadata?.enable_active_tracking
  )
}

function toLocalInputValue(date: Date) {
  const offsetMs = date.getTimezoneOffset() * 60 * 1000
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

function fromLocalInputValue(value: string) {
  if (!value) return undefined
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
}

function rangeForPreset(preset: TimeRangePreset, customFrom: string, customTo: string) {
  const now = new Date()
  if (preset === '30m') return { from: new Date(now.getTime() - 30 * 60 * 1000).toISOString(), to: now.toISOString() }
  if (preset === '2h') return { from: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(), to: now.toISOString() }
  if (preset === '12h') return { from: new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString(), to: now.toISOString() }
  if (preset === 'today') {
    const start = new Date(now)
    start.setHours(0, 0, 0, 0)
    return { from: start.toISOString(), to: now.toISOString() }
  }
  return {
    from: fromLocalInputValue(customFrom) || new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(),
    to: fromLocalInputValue(customTo) || now.toISOString(),
  }
}

function formatTimeValue(value: string | null | undefined, formatDateTime: ReturnType<typeof useI18n>['formatDateTime']) {
  if (!value) return '-'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : formatDateTime(parsed)
}

function escapeHtml(value: unknown) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function numberValue(value: unknown) {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function normalizePolygonPoint(point: unknown): [number, number] | null {
  if (Array.isArray(point)) {
    const lat = numberValue(point[0])
    const lon = numberValue(point[1])
    return validMapPoint(lat, lon) ? [lat!, lon!] : null
  }
  if (point && typeof point === 'object') {
    const row = point as Record<string, unknown>
    const lat = numberValue(row.lat ?? row.latitude)
    const lon = numberValue(row.lon ?? row.lng ?? row.longitude)
    return validMapPoint(lat, lon) ? [lat!, lon!] : null
  }
  return null
}

function polygonCenter(points: unknown[] | null | undefined): [number, number] | null {
  const normalized = (points || []).map(normalizePolygonPoint).filter(Boolean) as Array<[number, number]>
  if (!normalized.length) return null
  const totals = normalized.reduce((acc, point) => ({ lat: acc.lat + point[0], lon: acc.lon + point[1] }), { lat: 0, lon: 0 })
  const center: [number, number] = [totals.lat / normalized.length, totals.lon / normalized.length]
  return validMapPoint(center[0], center[1]) ? center : null
}

function selectedLocationCenter(location: AccessibleLocation): [number, number] | null {
  const raw = location as AccessibleLocation & Record<string, unknown>
  const circle = location.fence?.circle || location.circle
  const circleLat = numberValue(circle?.lat)
  const circleLon = numberValue(circle?.lon)
  if (validMapPoint(circleLat, circleLon)) return [circleLat!, circleLon!]

  const directLat = numberValue(raw.lat ?? raw.latitude ?? raw.centerLat ?? raw.center_lat)
  const directLon = numberValue(raw.lon ?? raw.lng ?? raw.longitude ?? raw.centerLon ?? raw.center_lon)
  if (validMapPoint(directLat, directLon)) return [directLat!, directLon!]

  return polygonCenter(location.fence?.polygon || location.polygon)
}

function locationSourceLabel(worker: LiveWorkerLocationDto, t: (text: string) => string) {
  const key = locationSourceKey(worker)
  if (key === 'location_fallback') return t('Location fallback')
  if (key === 'breadcrumb') return worker.isStale ? t('Stale breadcrumb') : t('Breadcrumb')
  if (validMapPoint(worker.lat, worker.lon)) return worker.isStale ? t('Stale GPS') : t('Live GPS')
  if (worker.missingGps) return t('Missing GPS')
  return worker.isStale ? t('Stale GPS') : t('Live GPS')
}

function workerDisplayName(worker?: LiveWorkerLocationDto | null) {
  return worker?.displayName || worker?.workerName || worker?.worker_name || worker?.name || 'Worker'
}

function workerInitials(worker?: LiveWorkerLocationDto | null) {
  return liveWorkerInitials(workerDisplayName(worker))
}

function workerBreadcrumbProfileId(worker?: LiveWorkerLocationDto | null) {
  return worker?.globalWorkerProfileId || worker?.global_worker_profile_id || worker?.workerProfileId || worker?.worker_profile_id || ''
}

function popupLine(label: string, value: unknown) {
  if (value === null || value === undefined || String(value).trim() === '' || String(value) === '-') return ''
  return `<span>${escapeHtml(label)}: ${escapeHtml(value)}</span>`
}

function workerOwnerLabel(worker: LiveWorkerLocationDto, t: (text: string) => string) {
  if (worker.workerOwnerType === 'agency') {
    return worker.agencyOrgName ? `${t('Agency')}: ${worker.agencyOrgName}` : t('Agency')
  }
  return t('Internal')
}

function workerPopupHtml(worker: LiveWorkerLocationDto, t: (text: string) => string, formatDateTime: ReturnType<typeof useI18n>['formatDateTime']) {
  const contactPhone = worker.actions?.canViewContact ? firstText(worker.contact?.phone, worker.phone, worker.workerPhone, worker.worker_phone) : ''
  const contactEmail = worker.actions?.canViewContact ? firstText(worker.contact?.email, worker.email, worker.workerEmail, worker.worker_email) : ''
  const assignment = firstText(worker.shift?.jobTitle, worker.assignmentLabel, worker.assignment_label, worker.jobTitle, worker.job_title, worker.role)
  const punchTime = firstText(worker.presence?.punchedInAt, worker.punchInAt, worker.punchedInAt, worker.punched_in_at, worker.timeIn, worker.time_in)
  const shiftStart = firstText(worker.shift?.startsAt, worker.shiftStartedAt, worker.shift_started_at, worker.startedAt, worker.started_at)
  const shiftEnd = firstText(worker.shift?.endsAt)
  const lastPunch = firstText(worker.lastPunchAt, worker.last_punch_at)
  const lastGps = firstText(worker.recordedAt, worker.capturedAt, worker.lastSeenAt)
  const onTheWayAt = firstText(worker.presence?.onTheWayAt, worker.onTheWayAt, worker.on_the_way_at)
  const onTheWayEta = firstText(worker.presence?.etaLabel, worker.onTheWayEtaLabel, worker.on_the_way_eta_label)
  const accuracy = worker.accuracyMeters !== null && worker.accuracyMeters !== undefined ? `${Math.round(Number(worker.accuracyMeters))} m` : ''
  const ownerLabel = worker.workerOwnerType ? workerOwnerLabel(worker, t) : ''
  return `
    <div class="live-worker-popup__identity">
      <span class="live-worker-popup__avatar"><span>${escapeHtml(workerInitials(worker))}</span>${worker.profilePhotoUrl ? `<img src="${escapeHtml(worker.profilePhotoUrl)}" alt="" onerror="this.remove()">` : ''}</span>
      <strong>${escapeHtml(workerDisplayName(worker))}</strong>
    </div>
      ${popupLine(t('Status'), t(workerStatusLabel(worker)))}
      ${ownerLabel ? popupLine(t('Worker type'), ownerLabel) : ''}
      ${popupLine(t('Phone'), contactPhone)}
      ${popupLine(t('Email'), contactEmail)}
      ${popupLine(t('Role/assignment'), assignment)}
      ${popupLine(t('Location'), worker.shift?.locationName || worker.locationName)}
      ${popupLine(t('Punched in'), punchTime ? formatTimeValue(punchTime, formatDateTime) : '')}
      ${popupLine(t('Shift start'), shiftStart ? formatTimeValue(shiftStart, formatDateTime) : '')}
      ${popupLine(t('Shift end'), shiftEnd ? formatTimeValue(shiftEnd, formatDateTime) : '')}
      ${popupLine(t('On the way'), onTheWayAt ? formatTimeValue(onTheWayAt, formatDateTime) : '')}
      ${popupLine(t('ETA'), onTheWayEta)}
      ${popupLine(t('Last punch'), lastPunch ? formatTimeValue(lastPunch, formatDateTime) : '')}
      ${popupLine(t('Last GPS'), lastGps ? formatTimeValue(lastGps, formatDateTime) : '')}
      ${popupLine(t('Accuracy'), accuracy)}
      ${popupLine(t('Source'), locationSourceLabel(worker, t))}
  `
}

function breadcrumbPointPopupHtml(point: NonNullable<WorkerBreadcrumbTrailDto['points']>[number], index: number, total: number, t: (text: string) => string, formatDateTime: ReturnType<typeof useI18n>['formatDateTime']) {
  const recordedAt = firstText(point.recordedAt, point.capturedAt)
  const accuracy = point.accuracyMeters !== null && point.accuracyMeters !== undefined ? `${Math.round(Number(point.accuracyMeters))} m` : ''
  return `
    <div class="mapper-popup">
      <strong>${escapeHtml(`${t('Point')} #${index + 1} ${t('of')} ${total}`)}</strong>
      ${popupLine(t('Time'), recordedAt ? formatTimeValue(recordedAt, formatDateTime) : '')}
      ${popupLine(t('Accuracy'), accuracy)}
      ${popupLine(t('Source'), point.source)}
      ${popupLine(t('Event'), point.eventType)}
      ${popupLine(t('Sequence'), firstText(point.sequence, point.order, point.index))}
    </div>
  `
}

export const BreadcrumbTrailViewer: React.FC<{
  location: AccessibleLocation
  enabled?: boolean
  onLocationUnavailable?: (message: string) => void
}> = ({ location, enabled = true, onLocationUnavailable }) => {
  const { t, formatDateTime, formatTime, formatNumber } = useI18n()
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.FeatureGroup | null>(null)
  const liveWorkersRequestInFlightRef = useRef(false)
  const liveWorkersAbortRef = useRef<AbortController | null>(null)
  const trailAbortRef = useRef<AbortController | null>(null)
  const pollingStoppedLocationRef = useRef('')
  const transientPollFailuresRef = useRef(0)
  const nextPollAtRef = useRef(0)
  const trailRequestRef = useRef(0)
  const liveWorkersRequestRef = useRef(0)
  const userMovedMapRef = useRef(false)
  const lastCenteredSelectionRef = useRef('')
  const messageOpeningRef = useRef(false)
  const [workers, setWorkers] = useState<LiveWorkerLocationDto[]>([])
  const [selectedWorkerId, setSelectedWorkerId] = useState(() => preservedViewerState.get(location.id)?.selectedWorkerId || '')
  const [trail, setTrail] = useState<WorkerBreadcrumbTrailDto | null>(null)
  const [trailWorkerId, setTrailWorkerId] = useState('')
  const [rangePreset, setRangePreset] = useState<TimeRangePreset>(() => preservedViewerState.get(location.id)?.rangePreset || '12h')
  const [customFrom, setCustomFrom] = useState(() => preservedViewerState.get(location.id)?.customFrom || toLocalInputValue(new Date(Date.now() - 12 * 60 * 60 * 1000)))
  const [customTo, setCustomTo] = useState(() => preservedViewerState.get(location.id)?.customTo || toLocalInputValue(new Date()))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [lastLoadedAt, setLastLoadedAt] = useState('')
  const [selectionNotice, setSelectionNotice] = useState('')
  const [workerDetail, setWorkerDetail] = useState<any | null>(null)
  const [shiftDetail, setShiftDetail] = useState<any | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [messageThreadId, setMessageThreadId] = useState('')
  const [messageOpening, setMessageOpening] = useState(false)

  const trackingEnabled = isLocationTrackingEnabled(location)
  const selectedWorker = workers.find((worker) => workerBreadcrumbProfileId(worker) === selectedWorkerId) || null
  const selectedRange = useMemo(() => rangeForPreset(rangePreset, customFrom, customTo), [rangePreset, customFrom, customTo])

  const loadLiveWorkers = async (options: { silent?: boolean } = {}) => {
    if (!enabled || !location.id) return
    if (pollingStoppedLocationRef.current === location.id) return
    if (liveWorkersRequestInFlightRef.current) return
    liveWorkersRequestInFlightRef.current = true
    const controller = new AbortController()
    const requestId = liveWorkersRequestRef.current + 1
    liveWorkersRequestRef.current = requestId
    liveWorkersAbortRef.current = controller
    if (!options.silent) setLoading(true)
    setError('')
    try {
      const response = await adminService.listLiveWorkersForLocation(location.id, { includeLatestOnly: true }, controller.signal)
      if (requestId !== liveWorkersRequestRef.current) return
      const rows = (response.data?.data || []) as LiveWorkerLocationDto[]
      transientPollFailuresRef.current = 0
      nextPollAtRef.current = 0
      setWorkers(rows)
      setSelectedWorkerId((current) => {
        const reconciled = reconcileSelectedLiveWorker(current, rows)
        if (!reconciled.disappeared) return reconciled.selectedWorkerId
        setSelectionNotice(t('This worker is no longer active on this map.'))
        return reconciled.selectedWorkerId
      })
      setLastLoadedAt(formatTime(new Date()))
    } catch (err) {
      if ((err as { code?: string })?.code === 'ERR_CANCELED') return
      const apiError = normalizeApiError(err, t('Unable to load live worker locations.'))
      if (apiError.status === 401) {
        pollingStoppedLocationRef.current = location.id
        setError(t('Your session could not be verified. Live tracking has stopped.'))
        try {
          await getAccessContext()
        } catch (sessionError) {
          if (normalizeApiError(sessionError).status === 401) {
            setApiAuthToken(null)
            setApiTenantContext(null)
            window.location.assign('/')
          }
        }
      } else if (apiError.status === 403 || apiError.status === 404) {
        pollingStoppedLocationRef.current = location.id
        const unavailableMessage = apiError.message || t('Live tracking is not available for this location.')
        setError(unavailableMessage)
        onLocationUnavailable?.(unavailableMessage)
      } else if (apiError.status && TRANSIENT_POLL_STATUSES.has(apiError.status)) {
        transientPollFailuresRef.current += 1
        if (transientPollFailuresRef.current >= MAX_TRANSIENT_POLL_FAILURES) {
          pollingStoppedLocationRef.current = location.id
          setError(t('Live tracking refresh paused after repeated temporary failures. Use Refresh to try again.'))
        } else {
          nextPollAtRef.current = Date.now() + LIVE_MAP_POLL_MS * (2 ** (transientPollFailuresRef.current - 1))
          setError(apiError.message)
        }
      } else {
        pollingStoppedLocationRef.current = location.id
        setError(apiError.message)
      }
    } finally {
      if (liveWorkersAbortRef.current === controller) {
        liveWorkersAbortRef.current = null
        liveWorkersRequestInFlightRef.current = false
        if (!options.silent) setLoading(false)
      }
    }
  }

  const loadTrail = async (workerProfileId = selectedWorkerId, options: { silent?: boolean } = {}) => {
    if (!location.id || !workerProfileId) {
      return
    }
    const requestId = trailRequestRef.current + 1
    trailRequestRef.current = requestId
    trailAbortRef.current?.abort()
    const controller = new AbortController()
    trailAbortRef.current = controller
    if (!options.silent) setLoading(true)
    setError('')
    try {
      const response = await adminService.listLocationBreadcrumbs(location.id, {
        workerProfileId,
        from: selectedRange.from,
        to: selectedRange.to,
        limit: 1000,
      }, controller.signal)
      const nextTrail = (response.data?.data || null) as WorkerBreadcrumbTrailDto | null
      if (requestId !== trailRequestRef.current) return
      setTrailWorkerId(workerProfileId)
      setTrail(nextTrail ? { ...nextTrail, workerProfileId: nextTrail.workerProfileId || workerProfileId } : null)
      setLastLoadedAt(formatTime(new Date()))
    } catch (err) {
      if ((err as { code?: string })?.code === 'ERR_CANCELED') return
      if (requestId === trailRequestRef.current) {
        setError(apiErrorMessage(err, t('Unable to load worker breadcrumb trail.')))
      }
    } finally {
      if (requestId === trailRequestRef.current) {
        if (trailAbortRef.current === controller) trailAbortRef.current = null
        if (!options.silent) setLoading(false)
      }
    }
  }

  useEffect(() => {
    pollingStoppedLocationRef.current = ''
    liveWorkersRequestRef.current += 1
    userMovedMapRef.current = false
    lastCenteredSelectionRef.current = ''
    transientPollFailuresRef.current = 0
    nextPollAtRef.current = 0
    setWorkers([])
    const saved = preservedViewerState.get(location.id)
    setSelectedWorkerId(saved?.selectedWorkerId || '')
    setTrail(null)
    setTrailWorkerId('')
    setError('')
    const initialLoadId = enabled ? window.setTimeout(() => loadLiveWorkers(), 0) : 0
    return () => {
      window.clearTimeout(initialLoadId)
      pollingStoppedLocationRef.current = location.id
      liveWorkersAbortRef.current?.abort()
      liveWorkersAbortRef.current = null
      liveWorkersRequestInFlightRef.current = false
      trailAbortRef.current?.abort()
      trailAbortRef.current = null
    }
  }, [enabled, location.id])

  useEffect(() => {
    if (selectedWorkerId) loadTrail(selectedWorkerId)
    else {
      setTrail(null)
      setTrailWorkerId('')
    }
  }, [selectedWorkerId, selectedRange.from, selectedRange.to])

  useEffect(() => {
    if (!enabled || !location.id) return
    const intervalId = window.setInterval(() => {
      if (pollingStoppedLocationRef.current === location.id || Date.now() < nextPollAtRef.current) return
      loadLiveWorkers({ silent: true })
      if (selectedWorkerId) {
        loadTrail(selectedWorkerId, { silent: true })
      }
    }, LIVE_MAP_POLL_MS)
    return () => window.clearInterval(intervalId)
  }, [enabled, location.id, selectedWorkerId, selectedRange.from, selectedRange.to])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return
    const saved = preservedViewerState.get(location.id)
    const map = L.map(mapContainerRef.current, { zoomControl: true, maxZoom: OSM_MAX_ZOOM }).setView(saved?.center || [0, 0], saved?.zoom || 2)
    map.on('dragstart', () => { userMovedMapRef.current = true })
    map.on('moveend', () => {
      if (!userMovedMapRef.current) return
      const center = map.getCenter()
      const current = preservedViewerState.get(location.id)
      preservedViewerState.set(location.id, { selectedWorkerId: current?.selectedWorkerId || '', rangePreset: current?.rangePreset || '12h', customFrom: current?.customFrom || '', customTo: current?.customTo || '', center: [center.lat, center.lng], zoom: map.getZoom() })
    })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: OSM_MAX_ZOOM,
      maxNativeZoom: OSM_MAX_ZOOM,
    }).addTo(map)
    layerRef.current = L.featureGroup().addTo(map)
    mapRef.current = map
    window.setTimeout(() => map.invalidateSize(), 0)
    return () => {
      map.remove()
      mapRef.current = null
      layerRef.current = null
    }
  }, [location.id])

  useEffect(() => {
    const current = preservedViewerState.get(location.id)
    preservedViewerState.set(location.id, { ...current, selectedWorkerId, rangePreset, customFrom, customTo })
  }, [location.id, selectedWorkerId, rangePreset, customFrom, customTo])

  useEffect(() => {
    if (!mapRef.current) return
    const timeout = window.setTimeout(() => mapRef.current?.invalidateSize(), 0)
    return () => window.clearTimeout(timeout)
  }, [location.id])

  useEffect(() => {
    const group = layerRef.current
    const map = mapRef.current
    if (!group || !map) return
    group.clearLayers()

    const fence = location.fence
    const circle = fence?.type === 'circle' ? fence.circle : undefined

    if (
      circle &&
      circle.lat != null &&
      circle.lon != null &&
      circle.radius != null
    ) {
      const lat = Number(circle.lat)
      const lon = Number(circle.lon)
      const radius = Number(circle.radius)

      if (Number.isFinite(lat) && Number.isFinite(lon) && Number.isFinite(radius) && radius > 0) {
        group.addLayer(L.circle([lat, lon], {
          radius,
          color: '#2563eb',
          fillColor: '#bfdbfe',
          fillOpacity: 0.18,
          weight: 2,
        }).bindPopup(escapeHtml(`${locationName(location)} ${t('geofence')}`)))
      }
    }
    if (fence?.type === 'polygon' && Array.isArray(fence.polygon) && fence.polygon.length) {
      group.addLayer(L.polygon(fence.polygon as L.LatLngExpression[], {
        color: '#2563eb',
        fillColor: '#bfdbfe',
        fillOpacity: 0.18,
        weight: 2,
      }).bindPopup(escapeHtml(`${locationName(location)} ${t('geofence')}`)))
    }

    workers.forEach((worker) => {
      if (!validMapPoint(worker.lat, worker.lon)) return
      const requestWorkerProfileId = workerBreadcrumbProfileId(worker)
      const selected = requestWorkerProfileId === selectedWorkerId
      const markerColors = workerStatusMarkerColors(worker)
      const initials = workerInitials(worker)
      const photo = worker.profilePhotoUrl
      const icon = L.divIcon({
        className: 'live-worker-marker-shell',
        html: `<span class="live-worker-marker${worker.isStale || worker.missingGps ? ' live-worker-marker--warning' : ''}" style="--pin-color:${escapeHtml(markerColors.color)}"><span class="live-worker-marker__initials">${escapeHtml(initials)}</span>${photo ? `<img src="${escapeHtml(photo)}" alt="" onerror="this.remove()">` : ''}</span>`,
        iconSize: [selected ? 48 : 42, selected ? 48 : 42],
        iconAnchor: [selected ? 24 : 21, selected ? 48 : 42],
        popupAnchor: [0, -44],
      })
      const marker = L.marker([worker.lat!, worker.lon!], { icon, title: workerDisplayName(worker), keyboard: true })
      const popup = document.createElement('div')
      popup.className = 'mapper-popup live-worker-popup'
      popup.innerHTML = workerPopupHtml(worker, t, formatDateTime)
      const actions = document.createElement('div')
      actions.className = 'mapper-popup__actions'
      const addAction = (label: string, enabled: boolean, action: () => void) => {
        if (!enabled) return
        const button = document.createElement('button')
        button.type = 'button'
        button.className = 'mapper-popup__button'
        button.textContent = label
        button.setAttribute('aria-label', label)
        button.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); action() })
        actions.appendChild(button)
      }
      addAction(t('Worker details'), Boolean(worker.actions?.canOpenWorker), () => void openWorkerDetails(worker))
      addAction(t('Shift details'), Boolean(worker.actions?.canOpenShift && worker.shift?.jobShiftId), () => void openShiftDetails(worker))
      addAction(t('Message worker'), Boolean(worker.actions?.canMessageWorker), () => void openMessageWorker(worker))
      addAction(t('Breadcrumbs'), Boolean(worker.actions?.canViewBreadcrumbs), () => setSelectedWorkerId(requestWorkerProfileId))
      if (actions.childElementCount) popup.appendChild(actions)
      marker.bindPopup(popup, { closeButton: true, autoPan: true })
      marker.on('click', () => setSelectedWorkerId(requestWorkerProfileId))
      group.addLayer(marker)
      if (selected) marker.openPopup()
    })

    const trailBelongsToSelectedWorker = Boolean(selectedWorkerId && trailWorkerId === selectedWorkerId)
    const points = trailBelongsToSelectedWorker ? trail?.points || [] : []
    if (points.length > 0) {
      const validPoints = points.filter((point) => validMapPoint(point.lat, point.lon))
      if (validPoints.length > 0) {
        const latLngs = validPoints.map((point) => [point.lat!, point.lon!] as [number, number])
        group.addLayer(L.polyline(latLngs, { color: '#0f766e', weight: 4, opacity: 0.78 }))
        const maxMarkers = 300
        const markerStep = validPoints.length > maxMarkers ? Math.ceil(validPoints.length / maxMarkers) : 1
        validPoints.forEach((point, index) => {
          const isFirst = index === 0
          const isLast = index === validPoints.length - 1
          if (!isFirst && !isLast && index % markerStep !== 0) return
          const marker = L.circleMarker([point.lat!, point.lon!], {
            radius: isFirst || isLast ? 6 : 4,
            color: '#0f766e',
            fillColor: isFirst ? '#ccfbf1' : isLast ? '#14b8a6' : '#5eead4',
            fillOpacity: 0.9,
            weight: 2,
          })
          marker.bindTooltip(isFirst ? t('Trail start') : isLast ? t('Trail end') : formatTimeValue(point.recordedAt || point.capturedAt, formatDateTime))
          marker.bindPopup(breadcrumbPointPopupHtml(point, index, validPoints.length, t, formatDateTime))
          group.addLayer(marker)
        })
      }
    }

    const validWorkerPins = workers.filter((worker) => validMapPoint(worker.lat, worker.lon) && !isFallbackLocation(worker) && !worker.missingGps)
    const selectedLivePoint = selectedWorker && validMapPoint(selectedWorker.lat, selectedWorker.lon) && !isFallbackLocation(selectedWorker) && !selectedWorker.missingGps
      ? [selectedWorker.lat!, selectedWorker.lon!] as [number, number]
      : null
    const trailPoints = points.filter((point) => validMapPoint(point.lat, point.lon))
    const latestTrailPoint = trailPoints.length ? trailPoints[trailPoints.length - 1] : null
    const fallbackTrailPoint = latestTrailPoint
      ? [latestTrailPoint.lat!, latestTrailPoint.lon!] as [number, number]
      : null
    const fenceCenter = selectedLocationCenter(location)
    const selectionChanged = lastCenteredSelectionRef.current !== selectedWorkerId
    if (selectionChanged) lastCenteredSelectionRef.current = selectedWorkerId
    if (userMovedMapRef.current && !selectionChanged) return
    if (!selectedWorkerId && fenceCenter) {
      map.setView(fenceCenter, Math.max(map.getZoom() || 0, 16))
      return
    }
    if (selectedLivePoint) {
      map.setView(selectedLivePoint, Math.max(map.getZoom() || 0, 16))
      return
    }
    if (validWorkerPins.length > 1) {
      map.fitBounds(L.latLngBounds(validWorkerPins.map((worker) => [worker.lat!, worker.lon!] as [number, number])).pad(0.18))
      return
    }
    if (validWorkerPins.length === 1) {
      map.setView([validWorkerPins[0].lat!, validWorkerPins[0].lon!], 16)
      return
    }
    if (fallbackTrailPoint) {
      map.setView(fallbackTrailPoint, Math.max(map.getZoom() || 0, 16))
      return
    }
    if (fenceCenter) {
      map.setView(fenceCenter, Math.max(map.getZoom() || 0, 16))
      return
    }
    const layers = group.getLayers()
    if (layers.length) map.fitBounds(group.getBounds().pad(0.18))
  }, [location, workers, selectedWorkerId, trail, trailWorkerId, t, formatDateTime])

  const trailBelongsToSelectedWorker = Boolean(selectedWorkerId && trailWorkerId === selectedWorkerId)
  const selectedTrailPoints = trailBelongsToSelectedWorker ? trail?.points || [] : []
  const selectedValidTrailPoints = selectedTrailPoints.filter((point) => validMapPoint(point.lat, point.lon))
  const workerCounts = useMemo(() => countLiveWorkers(workers), [workers])
  const restartPolling = () => {
    pollingStoppedLocationRef.current = ''
    transientPollFailuresRef.current = 0
    nextPollAtRef.current = 0
    loadLiveWorkers()
    if (selectedWorkerId) loadTrail(selectedWorkerId)
  }

  const openWorkerDetails = async (worker: LiveWorkerLocationDto) => {
    if (!worker.actions?.canOpenWorker) return
    setDetailLoading(true)
    setError('')
    try { setWorkerDetail(await getWorkerProfile(worker.workerProfileId)) }
    catch (err) { setError(apiErrorMessage(err, t('Worker details are no longer available.'))) }
    finally { setDetailLoading(false) }
  }

  const openShiftDetails = async (worker: LiveWorkerLocationDto) => {
    const shiftId = worker.shift?.jobShiftId || worker.jobShiftId
    if (!worker.actions?.canOpenShift || !shiftId) return
    setDetailLoading(true)
    setError('')
    try { setShiftDetail(await getScheduleShiftDetail(shiftId)) }
    catch (err) { setError(apiErrorMessage(err, t('Shift details are no longer available.'))) }
    finally { setDetailLoading(false) }
  }

  const openMessageWorker = async (worker: LiveWorkerLocationDto) => {
    if (!worker.actions?.canMessageWorker || messageOpeningRef.current) return
    messageOpeningRef.current = true
    setMessageOpening(true)
    setError('')
    try {
      const thread = await createMessageThread({
        threadType: 'direct_admin_worker', scopeType: 'direct', replyPolicy: 'participants_can_reply',
        workerProfileIds: [worker.workerProfileId], title: workerDisplayName(worker), firstMessageBody: null,
      })
      setMessageThreadId(thread.id)
    } catch (err) { setError(apiErrorMessage(err, t('Unable to open a conversation with this worker.'))) }
    finally { messageOpeningRef.current = false; setMessageOpening(false) }
  }

  return (
    <div className="admin-breadcrumb-viewer">
      <div className="admin-panel-toolbar">
        <div>
          <h3 style={{ margin: 0 }}>{t('Live Map / Breadcrumbs')}</h3>
          <p className="helper">{t('Location trails are available only while tracking is enabled and workers are on assigned shifts.')}</p>
        </div>
        <button type="button" className="btn btn-secondary" disabled={loading} onClick={restartPolling}>{t('Refresh')}</button>
      </div>
      <p className="helper">{t("Access is limited to authorized supervisors/admins for the worker's assigned location or organization.")}</p>
      {!enabled ? <ErrorBanner message={t('Live tracking is disabled by your platform administrator or plan.')} /> : null}
      {!trackingEnabled ? <ErrorBanner message={t('Tracking is disabled for this location. Enable live worker tracking for assigned shifts in Location Edit before expecting live worker movement.')} /> : null}
      <ErrorBanner message={error} />
      {selectionNotice ? <p className="admin-inline-notice" role="status">{selectionNotice}</p> : null}

      <div className="admin-form-grid">
        <label>{t('Worker')}<select className="input" value={selectedWorkerId} onChange={(event) => setSelectedWorkerId(event.target.value)}>
          <option value="">{t('Select worker')}</option>
          {workers.map((worker) => {
            const ownerSuffix = worker.workerOwnerType === 'agency'
              ? ` [${worker.agencyOrgName || t('Agency')}]`
              : ''
            const requestWorkerProfileId = workerBreadcrumbProfileId(worker)
            return <option key={requestWorkerProfileId || worker.workerProfileId} value={requestWorkerProfileId}>{worker.displayName || t('Worker')}{ownerSuffix} ({t(workerStatusLabel(worker))})</option>
          })}
        </select></label>
        <label>{t('Time range')}<select className="input" value={rangePreset} onChange={(event) => setRangePreset(event.target.value as TimeRangePreset)}>
          <option value="30m">{t('Last 30 min')}</option>
          <option value="2h">{t('Last 2 hours')}</option>
          <option value="12h">{t('Last 12 hours')}</option>
          <option value="today">{t('Today')}</option>
          <option value="custom">{t('Custom')}</option>
        </select></label>
        {rangePreset === 'custom' ? (
          <>
            <label>{t('From')}<input className="input" type="datetime-local" value={customFrom} onChange={(event) => setCustomFrom(event.target.value)} /></label>
            <label>{t('To')}<input className="input" type="datetime-local" value={customTo} onChange={(event) => setCustomTo(event.target.value)} /></label>
          </>
        ) : null}
      </div>

      {loading ? <LoadingState label={t('Loading map data...')} /> : null}
      <div ref={mapContainerRef} className="admin-breadcrumb-map" aria-label={t('Worker breadcrumb map')} />

      <div className="admin-summary-grid">
        <div><strong>{formatNumber(workerCounts.withGps)}</strong><span>{t('live workers with GPS points')}</span></div>
        <div><strong>{formatNumber(workerCounts.punchedIn)}</strong><span>{t('punched in')}</span></div>
        <div><strong>{formatNumber(workerCounts.onBreak)}</strong><span>{t('on break')}</span></div>
        <div><strong>{formatNumber(workerCounts.late)}</strong><span>{t('late')}</span></div>
        <div><strong>{formatNumber(workerCounts.fallback)}</strong><span>{t('fallback location')}</span></div>
        <div><strong>{formatNumber(workerCounts.stale)}</strong><span>{t('stale latest points')}</span></div>
        <div><strong>{formatNumber(selectedValidTrailPoints.length)}</strong><span>{t('selected trail points')}</span></div>
        <div><strong>{lastLoadedAt || '-'}</strong><span>{t('last refresh')}</span></div>
      </div>

      {workers.length > 0 ? (
        <div className="admin-chip-list" aria-label={t('Map status legend')}>
          <span className="status-pill status-pill--punched_in">{t('Punched In')}</span>
          <span className="status-pill status-pill--break">{t('Break')}</span>
          <span className="status-pill status-pill--late">{t('Late')}</span>
          <span className="status-pill status-pill--shift_ended">{t('Shift Ended')}</span>
          <span className="status-pill status-pill--missed_punch_out">{t('Missed Punch Out')}</span>
        </div>
      ) : null}

      {workers.length === 0 && !loading && !error ? <p className="helper">{t('No active worker location events exist for this location yet.')}</p> : null}
      {!selectedWorkerId && !loading && !error ? <p className="helper">{t('Select a worker to view breadcrumb trail.')}</p> : null}
      {selectedWorker && isFallbackLocation(selectedWorker) && !loading ? <p className="helper">{t('Live GPS unavailable; showing assigned location.')} {workerDisplayName(selectedWorker)}</p> : null}
      {selectedWorker && trailBelongsToSelectedWorker && (trail?.rawPointCount || 0) > 0 && !selectedValidTrailPoints.length && !loading ? <p className="helper">{t('Breadcrumbs returned, but none had valid coordinates.')} {workerDisplayName(selectedWorker)}</p> : null}
      {selectedWorker && trailBelongsToSelectedWorker && !(trail?.rawPointCount || 0) && !selectedValidTrailPoints.length && !loading ? <p className="helper">{t('No breadcrumb trail for this worker in the selected time range. Confirm the worker app is uploading location points and background location permission is enabled.')}</p> : null}
      {selectedWorker ? (
        <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
          <p><strong>{workerDisplayName(selectedWorker)}</strong></p>
          <div className="admin-summary-grid">
            <div><strong>{t(workerStatusLabel(selectedWorker))}</strong><span>{t('punch status')}</span></div>
            <div><strong>{locationSourceLabel(selectedWorker, t)}</strong><span>{t('location source')}</span></div>
            <div><strong>{formatTimeValue(firstText(selectedWorker.recordedAt, selectedWorker.capturedAt, selectedWorker.lastSeenAt), formatDateTime)}</strong><span>{t('last GPS update')}</span></div>
            <div><strong>{selectedWorker.accuracyMeters !== null && selectedWorker.accuracyMeters !== undefined ? `${formatNumber(Math.round(Number(selectedWorker.accuracyMeters)))} m` : '-'}</strong><span>{t('accuracy')}</span></div>
          </div>
          <p className="helper">
            {[firstText(selectedWorker.workerPhone, selectedWorker.phone), firstText(selectedWorker.workerEmail, selectedWorker.email), firstText(selectedWorker.assignmentLabel, selectedWorker.jobTitle, selectedWorker.role)].filter(Boolean).join(' | ') || t('No contact or assignment summary available.')}
          </p>
        </div>
      ) : null}
      <DetailDrawer title={workerDetail?.display_name || workerDetail?.workerName || t('Worker details')} isOpen={Boolean(workerDetail)} onClose={() => setWorkerDetail(null)}>
        {workerDetail ? <div className="stack"><p><strong>{t('Status')}</strong><br />{t(workerDetail.status || 'active')}</p><p><strong>{t('Phone')}</strong><br />{workerDetail.phone || '-'}</p><p><strong>{t('Email')}</strong><br />{workerDetail.workerEmail || workerDetail.email || '-'}</p></div> : null}
      </DetailDrawer>
      <DetailDrawer title={shiftDetail?.event?.title || t('Shift details')} isOpen={Boolean(shiftDetail)} onClose={() => setShiftDetail(null)}>
        {shiftDetail?.event ? <div className="stack"><p><strong>{t('Location')}</strong><br />{shiftDetail.event.locationName || '-'}</p><p><strong>{t('Scheduled start')}</strong><br />{formatTimeValue(shiftDetail.event.startsAt, formatDateTime)}</p><p><strong>{t('Scheduled end')}</strong><br />{formatTimeValue(shiftDetail.event.endsAt, formatDateTime)}</p><p><strong>{t('Status')}</strong><br />{t(shiftDetail.event.status || '-')}</p></div> : null}
      </DetailDrawer>
      <DetailDrawer title={t('Message worker')} isOpen={Boolean(messageThreadId)} onClose={() => setMessageThreadId('')}>
        {messageThreadId ? <div className="stack"><p>{t('Conversation ready.')}</p><a className="btn btn-primary" href={`/admin?workspace=messages&threadId=${encodeURIComponent(messageThreadId)}`}>{t('Open conversation')}</a></div> : null}
      </DetailDrawer>
      {detailLoading || messageOpening ? <span className="sr-only" role="status">{t('Loading...')}</span> : null}
    </div>
  )
}
